
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useAuraPoints } from '@/contexts/AuraPointsContext';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Star, 
  TrendingUp, 
  Shield, 
  Award, 
  Calendar, 
  Clock,
  Users
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { AuraPointsHistoryItem, AURA_LEVELS, getLevelInfo } from '@/models/AuraPoints';

const AuraPointsPage: React.FC = () => {
  const { currentUser, loading } = useAuth();
  const { points, loading: isLoading, refreshPoints } = useAuraPoints();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState('overview');
  
  useEffect(() => {
    if (!loading && !currentUser) {
      navigate('/login');
    }
    
    // Refresh points when the page loads
    if (currentUser && !isLoading) {
      refreshPoints();
    }
  }, [currentUser, loading, navigate, refreshPoints, isLoading]);
  
  if (loading || isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (!points) {
    return (
      <div className="container mx-auto px-4 py-10 text-center">
        <h2 className="text-2xl font-bold mb-4">No Aura Points data available</h2>
        <p className="text-muted-foreground mb-6">Start engaging with other users to earn Aura Points!</p>
        <Button onClick={() => navigate('/chat')}>Start a Chat</Button>
      </div>
    );
  }
  
  const levelInfo = getLevelInfo(points.totalPoints);
  
  // Format date nicely
  const formatDate = (dateString: string) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };
  
  // Group history by date
  const groupHistoryByDate = (history: AuraPointsHistoryItem[]) => {
    const grouped: {[key: string]: AuraPointsHistoryItem[]} = {};
    
    history.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      .forEach(item => {
        const date = new Date(item.timestamp).toLocaleDateString();
        if (!grouped[date]) {
          grouped[date] = [];
        }
        grouped[date].push(item);
      });
    
    return grouped;
  };
  
  const groupedHistory = groupHistoryByDate(points.history || []);
  
  // Get appropriate icon for each reason
  const getReasonIcon = (reason: string) => {
    switch(reason) {
      case 'rating_received': return <Star className="w-4 h-4 text-yellow-500" />;
      case 'rating_given': return <Star className="w-4 h-4 text-blue-500" />;
      case 'engagement': return <Clock className="w-4 h-4 text-green-500" />;
      case 'daily_bonus': return <Calendar className="w-4 h-4 text-purple-500" />;
      case 'profile_completion': return <Users className="w-4 h-4 text-indigo-500" />;
      case 'achievement_unlocked': return <Award className="w-4 h-4 text-amber-500" />;
      case 'streak_bonus': return <TrendingUp className="w-4 h-4 text-red-500" />;
      default: return <Shield className="w-4 h-4 text-gray-500" />;
    }
  };
  
  return (
    <div className={cn(
      "container mx-auto px-4 py-8",
      theme === 'dark' ? 'text-slate' : 'text-black',
      isMobile ? "max-w-full" : "max-w-5xl"
    )}>
      <div className="mb-6 pt-4">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft size={16} />
          Back
        </Button>
      </div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="space-y-8"
      >
        <h1 className={cn(
          "text-3xl font-bold mb-8 text-center",
          theme === 'dark' ? "text-slate-200" : "text-gray-800"
        )}>
          Your Aura Points
        </h1>
        
        <div className="grid gap-6 md:grid-cols-3">
          <Card className={cn(
            "overflow-hidden border-foreground/10 shadow-md col-span-3 md:col-span-1",
            theme === 'dark' ? 'bg-midnight/80' : 'bg-white/95 backdrop-blur-sm'
          )}>
            <CardHeader className="pb-4">
              <CardTitle className="text-xl">Level {levelInfo.level}: {levelInfo.name}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col items-center justify-center space-y-4">
                <div 
                  className="w-32 h-32 rounded-full flex items-center justify-center text-4xl font-bold border-8"
                  style={{ borderColor: levelInfo.color, color: levelInfo.color }}
                >
                  {levelInfo.level}
                </div>
                
                <div className="text-center space-y-1">
                  <div className="text-3xl font-bold">{points.totalPoints}</div>
                  <div className="text-sm text-muted-foreground">Total Aura Points</div>
                </div>
                
                {levelInfo.nextLevel && (
                  <div className="w-full space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Progress to {levelInfo.nextLevel}</span>
                      <span>{levelInfo.progress.current}/{levelInfo.progress.target}</span>
                    </div>
                    <Progress 
                      value={(levelInfo.progress.current / levelInfo.progress.target) * 100} 
                      className="h-2"
                    />
                    <p className="text-xs text-muted-foreground text-center">
                      {levelInfo.pointsToNextLevel} more points needed
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          
          <Card className={cn(
            "overflow-hidden border-foreground/10 shadow-md col-span-3 md:col-span-2",
            theme === 'dark' ? 'bg-midnight/80' : 'bg-white/95 backdrop-blur-sm'
          )}>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <CardHeader className="pb-0">
                <div className="flex justify-between items-center">
                  <CardTitle className="text-xl">Aura Stats</CardTitle>
                  <TabsList>
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="history">History</TabsTrigger>
                    <TabsTrigger value="achievements">Achievements</TabsTrigger>
                  </TabsList>
                </div>
              </CardHeader>
              
              <CardContent>
                <TabsContent value="overview" className="mt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <Star className="w-5 h-5 text-yellow-500" />
                        <div>
                          <div className="text-sm text-muted-foreground">Average Rating</div>
                          <div className="text-xl font-semibold">{points.averageRating.toFixed(1)}/5.0</div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <Users className="w-5 h-5 text-blue-500" />
                        <div>
                          <div className="text-sm text-muted-foreground">Ratings Received</div>
                          <div className="text-xl font-semibold">{points.ratingsReceived}</div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <Star className="w-5 h-5 text-green-500" />
                        <div>
                          <div className="text-sm text-muted-foreground">Ratings Given</div>
                          <div className="text-xl font-semibold">{points.ratingsGiven}</div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <Calendar className="w-5 h-5 text-purple-500" />
                        <div>
                          <div className="text-sm text-muted-foreground">Last Updated</div>
                          <div className="text-xl font-semibold">{formatDate(points.lastUpdated)}</div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <Award className="w-5 h-5 text-amber-500" />
                        <div>
                          <div className="text-sm text-muted-foreground">Achievements Unlocked</div>
                          <div className="text-xl font-semibold">
                            {points.achievements?.filter(a => a.unlockedAt).length || 0} / {points.achievements?.length || 0}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <TrendingUp className="w-5 h-5 text-red-500" />
                        <div>
                          <div className="text-sm text-muted-foreground">Current Streak</div>
                          <div className="text-xl font-semibold">{points.streak?.currentStreak || 0} days</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="history" className="mt-4">
                  <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
                    {Object.keys(groupedHistory).length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <p>No activity history yet</p>
                        <p className="text-sm">Start interacting to earn Aura Points!</p>
                      </div>
                    ) : (
                      Object.entries(groupedHistory).map(([date, items]) => (
                        <div key={date} className="space-y-2">
                          <h3 className="text-sm font-semibold sticky top-0 bg-background py-1 z-10">{date}</h3>
                          {items.map((item) => (
                            <div key={item.id} className="flex items-start gap-3 py-2 border-b border-border last:border-0">
                              <div className="mt-1">{getReasonIcon(item.reason)}</div>
                              <div className="flex-1">
                                <div className="text-sm font-medium">{item.details || getReasonTitle(item.reason)}</div>
                                <div className="text-xs text-muted-foreground">
                                  {new Date(item.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                </div>
                              </div>
                              <div className={cn(
                                "text-sm font-semibold",
                                item.points > 0 ? 'text-green-500' : 'text-red-500'
                              )}>
                                {item.points > 0 ? '+' : ''}{item.points}
                              </div>
                            </div>
                          ))}
                        </div>
                      ))
                    )}
                  </div>
                </TabsContent>
                
                <TabsContent value="achievements" className="mt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[400px] overflow-y-auto pr-2">
                    {points.achievements && points.achievements.length > 0 ? 
                      points.achievements.map(achievement => (
                        <div 
                          key={achievement.id} 
                          className={cn(
                            "p-4 rounded-lg border flex items-start gap-3",
                            achievement.unlockedAt 
                              ? "border-green-500/20 bg-green-500/5" 
                              : "border-gray-300/20 bg-gray-100/5"
                          )}
                        >
                          <div className={cn(
                            "w-10 h-10 rounded-full flex items-center justify-center",
                            achievement.unlockedAt 
                              ? "bg-green-500/10 text-green-500" 
                              : "bg-gray-300/10 text-gray-400"
                          )}>
                            {getAchievementIcon(achievement.icon)}
                          </div>
                          <div className="flex-1">
                            <div className="flex justify-between">
                              <div className="font-medium">{achievement.name}</div>
                              <div className="text-xs font-semibold text-green-500">
                                {achievement.unlockedAt ? `+${achievement.pointsAwarded}` : ''}
                              </div>
                            </div>
                            <div className="text-xs text-muted-foreground">{achievement.description}</div>
                            {achievement.progress && (
                              <div className="mt-2 space-y-1">
                                <Progress 
                                  value={(achievement.progress.current / achievement.progress.target) * 100} 
                                  className="h-1.5"
                                />
                                <div className="text-xs text-right text-muted-foreground">
                                  {achievement.progress.current}/{achievement.progress.target}
                                </div>
                              </div>
                            )}
                            {achievement.unlockedAt && (
                              <div className="text-xs text-muted-foreground mt-1">
                                Unlocked: {formatDate(achievement.unlockedAt)}
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    : (
                      <div className="col-span-2 text-center py-8 text-muted-foreground">
                        <p>No achievements yet</p>
                        <p className="text-sm">Keep using the app to unlock achievements!</p>
                      </div>
                    )}
                  </div>
                </TabsContent>
              </CardContent>
            </Tabs>
          </Card>
        </div>
      </motion.div>
    </div>
  );
};

// Helper functions for the component
function getReasonTitle(reason: string): string {
  switch(reason) {
    case 'rating_received': return 'Rating Received';
    case 'rating_given': return 'Rating Given';
    case 'engagement': return 'Chat Engagement';
    case 'daily_bonus': return 'Daily Bonus';
    case 'profile_completion': return 'Profile Completed';
    case 'achievement_unlocked': return 'Achievement Unlocked';
    case 'streak_bonus': return 'Streak Bonus';
    default: return 'Other Activity';
  }
}

function getAchievementIcon(iconName: string) {
  switch(iconName) {
    case 'star': return <Star className="w-5 h-5" />;
    case 'users': return <Users className="w-5 h-5" />;
    case 'calendar': return <Calendar className="w-5 h-5" />;
    case 'timer': return <Clock className="w-5 h-5" />;
    case 'award': return <Award className="w-5 h-5" />;
    case 'message-circle': return <Clock className="w-5 h-5" />;
    case 'user-check': return <Users className="w-5 h-5" />;
    default: return <Award className="w-5 h-5" />;
  }
}

export default AuraPointsPage;
